import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
  Divider,
  useMediaQuery,
  useTheme,
  Paper,
} from "@mui/material";
import axios from "axios";

const WeatherCard = ({ lat, lon }) => {
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(true);

  const apiKey = "9425d3f5a8a01fb31de8543e8cce0c71"; // ✅ OpenWeather key
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ Fetch 7-day forecast
  const fetchWeatherData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://api.openweathermap.org/data/3.0/onecall?lat=${lat}&lon=${lon}&appid=${apiKey}&units=metric`
      );

      const daily = res?.data?.daily?.slice(0, 6) || [];
      const formatted = daily.map((day) => ({
        date: new Date(day.dt * 1000).toLocaleDateString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
        }),
        temp: day.temp.day,
        humidity: day.humidity,
        rain: day.rain || 0,
        wind: day.wind_speed,
        uv: day.uvi,
        clouds: day.clouds,
        sunrise: new Date(day.sunrise * 1000).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        sunset: new Date(day.sunset * 1000).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));

      setForecast(formatted);
    } catch (error) {
      console.error("Weather fetch failed:", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (lat && lon) fetchWeatherData();
  }, [lat, lon]);

  // 🌿 Simple risk level helpers
  const getPestRisk = (h, r) => {
    if (h > 80 && r > 15) return { label: "High", color: "#ef4444" };
    if (h > 70 && r > 5) return { label: "Medium", color: "#f59e0b" };
    return { label: "Low", color: "#22c55e" };
  };

  const getAlert = (t, h) => {
    if (t > 35 && h > 75) return "⚠️ Heat Stress";
    if (h > 85) return "💧 High Humidity";
    if (t < 10) return "❄️ Cold Alert";
    return null;
  };

  // ✅ UI
  return (
    <Paper
      elevation={3}
      sx={{
        borderRadius: 4,
        background: "linear-gradient(180deg, #ffffff, #f9fafb)",
        p: { xs: 2.5, sm: 4 },
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
      }}
    >
      {/* <Typography
        variant="h5"
        fontWeight={600}
        textAlign="center"
        gutterBottom
        sx={{
          mb: 3,
          color: "#0f172a",
          letterSpacing: 0.5,
        }}
      >
        🌾 6-Day Farm Weather Forecast & Risk Alerts
      </Typography> */}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={50} />
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {forecast.map((day, idx) => {
            const risk = getPestRisk(day.humidity, day.rain);
            const alert = getAlert(day.temp, day.humidity);
            return (
              <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 3,
                    p: 2,
                    height: "100%",
                    backgroundColor: "#ffffff",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Header */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ fontSize: { xs: 15, sm: 16 } }}
                    >
                      {day.date}
                    </Typography>
                    <Chip
                      label={risk.label}
                      size="small"
                      sx={{
                        backgroundColor: risk.color,
                        color: "#fff",
                        fontWeight: 600,
                        height: 24,
                      }}
                    />
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Info Section */}
                  <Box sx={{ fontSize: { xs: 13, sm: 14 }, lineHeight: 1.9 }}>
                    <Tooltip title="Day Temperature">
                      <Typography>🌡️ {day.temp.toFixed(1)}°C</Typography>
                    </Tooltip>
                    <Tooltip title="Humidity">
                      <Typography>💧 {day.humidity}%</Typography>
                    </Tooltip>
                    <Tooltip title="Rainfall">
                      <Typography>🌧️ {day.rain.toFixed(1)} mm</Typography>
                    </Tooltip>
                    <Tooltip title="Wind Speed">
                      <Typography>🌬️ {day.wind.toFixed(1)} m/s</Typography>
                    </Tooltip>
                    <Tooltip title="Cloud Cover">
                      <Typography>☁️ {day.clouds}%</Typography>
                    </Tooltip>
                    <Tooltip title="UV Index">
                      <Typography>🔆 UV: {day.uv}</Typography>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      🌅 {day.sunrise} → 🌇 {day.sunset}
                    </Typography>
                  </Box>

                  {/* Alert */}
                  {alert && (
                    <Chip
                      label={alert}
                      size="small"
                      sx={{
                        mt: 1.5,
                        backgroundColor: "#fff7ed",
                        color: "#b45309",
                        border: "1px solid #fdba74",
                        fontWeight: 600,
                        borderRadius: 1.5,
                        fontSize: 12,
                      }}
                    />
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Paper>
  );
};

export default WeatherCard;
